import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, Legend } from "recharts";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { Leaf, TrendingUp, Folder, Wrench, Package, BarChart3 } from "lucide-react";

const PALETTE = ["#7B3F2C", "#5C7444", "#A06C4F", "#3B4A39", "#C97C5D", "#94A38C", "#6B4B36", "#2F3A2C"];

export default function Co2eDashboard() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading || !user) return;
    api.get("/dashboard/co2e", { params: { months: 12 } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Error"));
  }, [user, loading]);

  if (loading || !user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;
  if (err) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{err}</div></Layout>;
  if (!data) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;

  const { totals, per_project, per_month, per_category, top_machines, top_materials, hyra_vs_kop } = data;

  const compareData = [
    { name: t("co2eDash.kop"), kg: hyra_vs_kop.kop_kg_om_kopt_nytt, fill: "#A06C4F" },
    { name: t("co2eDash.hyra"), kg: hyra_vs_kop.hyra_kg_faktisk, fill: "#5C7444" },
  ];

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16" data-testid="co2e-dashboard-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-tech">{t("co2eDash.label")}</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("co2eDash.title")}</h1>
            <p className="mt-2 text-[var(--ink-700)]">{t("co2eDash.subtitle")}</p>
          </div>
        </div>

        {/* Top totals */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <Stat icon={<Leaf size={14} />} label={t("co2eDash.totalSaved")} value={`${totals.co2e_total_kg} kg`} highlight testId="stat-total" />
          <Stat icon={<Package size={14} />} label={t("co2eDash.materialSaved")} value={`${totals.material_co2e_kg} kg`} testId="stat-material" />
          <Stat icon={<Wrench size={14} />} label={t("co2eDash.machineSaved")} value={`${totals.machine_co2e_kg} kg`} testId="stat-machine" />
          <Stat icon={<Folder size={14} />} label={t("co2eDash.projects")} value={totals.projects_count} testId="stat-projects" />
        </div>
        <div className="label-tech mt-2">{t("co2eDash.kalla")}</div>

        {/* Time series + per category side by side */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <Card title={t("co2eDash.overTime")} icon={<TrendingUp size={14} />} testId="card-over-time">
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                <LineChart data={per_month} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke="#E2DDD3" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#6B5C44" fontSize={11} />
                  <YAxis stroke="#6B5C44" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#1A1815", color: "#F4EFE5", border: 0, fontSize: 12 }} />
                  <Line type="monotone" dataKey="co2e_kg" stroke="#7B3F2C" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title={t("co2eDash.perCategory")} icon={<BarChart3 size={14} />} testId="card-per-category">
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                <BarChart data={per_category.slice(0, 10)} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke="#E2DDD3" strokeDasharray="3 3" />
                  <XAxis dataKey="category" stroke="#6B5C44" fontSize={10} />
                  <YAxis stroke="#6B5C44" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#1A1815", color: "#F4EFE5", border: 0, fontSize: 12 }} />
                  <Bar dataKey="co2e_kg" radius={[2, 2, 0, 0]}>
                    {per_category.slice(0, 10).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Hyra vs Kop + Per project */}
        <div className="mt-px grid grid-cols-1 lg:grid-cols-2 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <Card title={t("co2eDash.hyraVsKop")} testId="card-hyra-vs-kop">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                <BarChart data={compareData} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke="#E2DDD3" strokeDasharray="3 3" />
                  <XAxis dataKey="name" stroke="#6B5C44" fontSize={11} />
                  <YAxis stroke="#6B5C44" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#1A1815", color: "#F4EFE5", border: 0, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="kg" radius={[2, 2, 0, 0]}>
                    {compareData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="label-tech mt-3">{t("co2eDash.besparing")}: <span className="font-display text-base text-[var(--moss)]">{hyra_vs_kop.besparing_kg} kg</span></div>
          </Card>

          <Card title={t("co2eDash.perProject")} icon={<Folder size={14} />} testId="card-per-project">
            <div className="h-[260px] overflow-y-auto">
              {per_project.length === 0 ? (
                <div className="label-tech">{t("co2eDash.noProjects")}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="label-tech border-b border-[var(--ink-200)]">
                      <th className="text-left p-2">{t("prj.namn")}</th>
                      <th className="text-right p-2">CO2e</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...per_project].sort((a, b) => b.co2e_kg - a.co2e_kg).map((p) => (
                      <tr key={p.id} className="border-b border-[var(--ink-200)]" data-testid={`project-row-${p.id}`}>
                        <td className="p-2"><Link to={`/projects/${p.id}`} className="hover:text-[var(--brick)]">{p.namn}</Link></td>
                        <td className="p-2 text-right font-display">{p.co2e_kg} kg</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </div>

        {/* Top 10 machines + top 10 materials */}
        <div className="mt-px grid grid-cols-1 lg:grid-cols-2 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <Card title={t("co2eDash.top10Machines")} icon={<Wrench size={14} />} testId="card-top-machines">
            {top_machines.length === 0 ? <div className="label-tech">{t("co2eDash.noMachines")}</div> : (
              <table className="w-full text-sm">
                <thead><tr className="label-tech border-b border-[var(--ink-200)]"><th className="text-left p-2">{t("mach.namn")}</th><th className="text-left p-2">{t("mach.typ")}</th><th className="text-right p-2">CO2e</th></tr></thead>
                <tbody>
                  {top_machines.map((m) => (
                    <tr key={m.id} className="border-b border-[var(--ink-200)]" data-testid={`top-machine-${m.id}`}>
                      <td className="p-2"><Link to={`/machines/${m.id}`} className="hover:text-[var(--brick)]">{m.namn}</Link></td>
                      <td className="p-2 label-tech">{m.typ}</td>
                      <td className="p-2 text-right font-display">{m.co2e_kg} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card title={t("co2eDash.top10Materials")} icon={<Package size={14} />} testId="card-top-materials">
            {top_materials.length === 0 ? <div className="label-tech">{t("co2eDash.noMaterials")}</div> : (
              <table className="w-full text-sm">
                <thead><tr className="label-tech border-b border-[var(--ink-200)]"><th className="text-left p-2">{t("add.titleField")}</th><th className="text-left p-2">{t("add.categoryField")}</th><th className="text-right p-2">CO2e</th></tr></thead>
                <tbody>
                  {top_materials.map((m) => (
                    <tr key={m.id} className="border-b border-[var(--ink-200)]" data-testid={`top-material-${m.id}`}>
                      <td className="p-2"><Link to={`/listings/${m.id}`} className="hover:text-[var(--brick)]">{m.title}</Link></td>
                      <td className="p-2 label-tech">{m.category}</td>
                      <td className="p-2 text-right font-display">{m.co2e_kg} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}

function Stat({ icon, label, value, highlight, testId }) {
  return (
    <div className={`p-5 ${highlight ? "bg-[var(--moss)] text-[var(--bone)]" : "bg-[var(--surface)]"}`} data-testid={testId}>
      <div className="label-tech opacity-80 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="font-display text-2xl mt-2">{value}</div>
    </div>
  );
}

function Card({ title, icon, children, testId }) {
  return (
    <div className="bg-[var(--bone)] p-5" data-testid={testId}>
      <div className="label-tech inline-flex items-center gap-1">{icon} {title}</div>
      <div className="mt-4">{children}</div>
    </div>
  );
}
